import { useLoaderData } from "react-router";

import {
  api,
  ApiError,
  type MeResponse,
  type SiteDetail as SiteDetailShape,
} from "../api/client.js";
import { EditorScreen, type EditorSource } from "../editor/EditorScreen.js";

interface LoaderData {
  readonly site: SiteDetailShape;
  readonly session: MeResponse;
}

/** Cloud site route `/sites/:id` — server-owned, requires a session. */
export async function siteDetailLoader({
  params,
}: {
  params: { id?: string };
}): Promise<LoaderData> {
  const id = params.id;
  if (!id) throw new Response("not found", { status: 404 });

  // Cloud sites need a session. A hard nav (not RR redirect) triggers the
  // openauth dance and returns to this site; stall so we never render
  // unauth state mid-navigation.
  const signIn = async (): Promise<never> => {
    const returnTo = `/sites/${encodeURIComponent(id)}`;
    window.location.assign(
      `/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`,
    );
    await new Promise(() => {});
    throw new Response("unauthorized", { status: 401 });
  };

  const me = await fetch("/api/me", { credentials: "same-origin" }).then((r) =>
    r.status === 401 ? signIn() : r.json(),
  );
  const site = await api.getSite(id).catch((e) => {
    if (e instanceof ApiError && e.status === 401) return signIn();
    if (e instanceof ApiError && e.status === 404) {
      throw new Response("not found", { status: 404 });
    }
    throw e;
  });
  return { site, session: me };
}

export function SiteDetail() {
  const { site, session } = useLoaderData() as LoaderData;
  const source: EditorSource = {
    id: site.id,
    name: site.name,
    slug: site.slug,
    role: site.role,
    isLocal: false,
  };
  return <EditorScreen key={site.id} source={source} session={session} />;
}
